import { Sparkles } from "lucide-react";
import type { ElementType, ReactNode } from "react";

export interface GamjaHomeHeroProps {
  badge: string;
  title: ReactNode;
  description: ReactNode;
  primaryAction: ReactNode;
  secondaryAction?: ReactNode;
  mascot: ReactNode;
  headingClassName?: string;
  /**
   * 기본 배경 위·본문 아래에 까는 장식 레이어(시즌 스킨 등).
   * 섹션 전체를 덮으려면 `absolute inset-0` 요소로 넘긴다.
   */
  backdrop?: ReactNode;
}

/**
 * 감자 패밀리 홈의 중앙 히어로.
 *
 * 상품별 문구·CTA·마스코트만 주입하고 레이아웃과 반응형 규칙은 한곳에서 유지한다.
 */
export function GamjaHomeHero({
  badge,
  title,
  description,
  primaryAction,
  secondaryAction,
  mascot,
  headingClassName,
  backdrop,
}: GamjaHomeHeroProps) {
  return (
    <section className="relative mb-8 overflow-hidden rounded-[28px] border border-amber-200/80 bg-gradient-to-br from-amber-50 via-orange-50/60 to-white p-6 shadow-sm sm:p-7 dark:border-amber-900/60 dark:from-amber-950/40 dark:via-orange-950/20 dark:to-neutral-900">
      <div
        aria-hidden="true"
        className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-200/40 blur-2xl dark:bg-amber-700/20"
      />
      {backdrop}
      <div className="relative flex items-center gap-6">
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-bold text-amber-700 shadow-sm ring-1 ring-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:ring-amber-800">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {badge}
          </span>
          <h1
            className={`${headingClassName ?? ""} mt-3 text-3xl leading-tight text-foreground sm:text-4xl`}
          >
            {title}
          </h1>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            {description}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            {primaryAction}
            {secondaryAction}
          </div>
        </div>
        {mascot}
      </div>
    </section>
  );
}

export interface GamjaHomeSubject {
  name: string;
  href: string;
  visual: ReactNode;
}

export interface GamjaHomeSubjectGroup {
  tone: "sky" | "amber";
  label: string;
  description: string;
  subjects: readonly GamjaHomeSubject[];
  columns?: 4 | 5;
}

export interface GamjaSubjectBrowserProps {
  title?: string;
  titleClassName?: string;
  help?: ReactNode;
  groups: readonly GamjaHomeSubjectGroup[];
  linkComponent?: ElementType;
}

/**
 * 감자 패밀리 홈의 과목 탐색 카드.
 *
 * 과목 분류와 링크는 앱이 소유하고, 카드 형태·톤·그리드만 공유한다.
 */
export function GamjaSubjectBrowser({
  title = "과목별로 찾아보기",
  titleClassName,
  help,
  groups,
  linkComponent: SubjectLink = "a",
}: GamjaSubjectBrowserProps) {
  return (
    <section className="my-8">
      <div className="mb-4 flex items-center justify-center gap-2">
        <h2 className={`text-center text-2xl ${titleClassName ?? ""}`}>
          {title}
        </h2>
        {help}
      </div>

      <div className="mx-auto max-w-4xl space-y-4 px-4">
        {groups.map((group) => {
          const isSky = group.tone === "sky";
          const gridColumns =
            group.columns === 4
              ? "grid-cols-2 sm:grid-cols-4"
              : "grid-cols-3 sm:grid-cols-5";

          return (
            <div
              key={group.label}
              className={`rounded-3xl border p-4 sm:p-5 ${
                isSky
                  ? "border-sky-200 bg-sky-50/50 dark:border-sky-900 dark:bg-sky-950/20"
                  : "border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20"
              }`}
            >
              <div className="mb-4 flex items-center gap-2.5">
                <span
                  className={`${titleClassName ?? ""} grid h-9 place-items-center rounded-full px-3.5 text-base font-semibold text-white ${
                    isSky ? "bg-sky-500" : "bg-amber-500"
                  }`}
                >
                  {group.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {group.description}
                </span>
              </div>
              <div className={`grid gap-4 sm:gap-5 ${gridColumns}`}>
                {group.subjects.map((subject) => (
                  <SubjectLink
                    key={subject.name}
                    href={subject.href}
                    className="group flex flex-col items-center rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                  >
                    <div className="relative aspect-square w-full overflow-hidden rounded-[26%] bg-white shadow-sm ring-1 ring-black/5 motion-safe:transition-[transform,box-shadow] motion-safe:duration-200 motion-safe:group-hover:-translate-y-1 motion-safe:group-hover:rotate-[-3deg] group-hover:shadow-lg dark:bg-neutral-800">
                      {subject.visual}
                    </div>
                    <span className="mt-1.5 text-center text-[13px] font-semibold leading-tight sm:text-sm">
                      {subject.name}
                    </span>
                  </SubjectLink>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
